import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check as CheckIcon } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  BookOpen, ChevronLeft, ChevronRight, Loader2, Search, Copy, Check,
  LogIn, LogOut, Star, ChevronDown, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { findBibleReferences } from '../utils/bibleReferences';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Flag for each language (used to differentiate ES/EN/PT versions in the dropdown)
const langFlag = (tag) => {
  const t = (tag || '').toLowerCase();
  if (t.startsWith('spa') || t.startsWith('es')) return '🇪🇸';
  if (t.startsWith('eng') || t.startsWith('en')) return '🇺🇸';
  if (t.startsWith('por') || t.startsWith('pt')) return '🇧🇷';
  return '🌐';
};

// authHeader removed: axios interceptor in App.js injects the token automatically.

// localStorage keys for "last read" memory
const LS_BIBLE = 'yv_last_bible';
const LS_BOOK = 'yv_last_book';
const LS_CHAPTER = 'yv_last_chapter';

const BibliaYouVersion = () => {
  const [bibles, setBibles] = useState([]);
  const [bibleId, setBibleId] = useState(null);
  const [index, setIndex] = useState(null); // { books: [...] }
  const [bookId, setBookId] = useState(null);
  const [chapterId, setChapterId] = useState(null);
  const [passage, setPassage] = useState(null); // { reference, content }
  const [loadingBibles, setLoadingBibles] = useState(true);
  const [loadingIndex, setLoadingIndex] = useState(false);
  const [loadingPassage, setLoadingPassage] = useState(false);
  const [searchRef, setSearchRef] = useState('');
  const [copied, setCopied] = useState(false);

  // Multi-version comparison state (max 2 additional, total 3 columns)
  const [compareBibleIds, setCompareBibleIds] = useState(() => {
    try {
      const stored = localStorage.getItem('yv_compare_bibles');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [comparePassages, setComparePassages] = useState({}); // {bibleId: {reference, content}}
  const [loadingCompare, setLoadingCompare] = useState({}); // {bibleId: bool}

  // YouVersion OAuth connection state
  const [yvStatus, setYvStatus] = useState({ connected: false });
  const [connecting, setConnecting] = useState(false);
  const [highlights, setHighlights] = useState(null);
  const [loadingHighlights, setLoadingHighlights] = useState(false);
  const [showHighlights, setShowHighlights] = useState(false);

  // Load connection status on mount
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const res = await axios.get(`${API}/youversion/auth/status`);
        setYvStatus(res.data || { connected: false });
      } catch (e) {
        console.error('YV status error', e);
      }
    };
    loadStatus();
  }, []);

  const handleConnect = async () => {
    // YouVersion OAuth solo está registrado para producción (ibaconnect.info).
    // En preview el redirect_uri no coincide y YouVersion devuelve invalid_request.
    const isProduction = window.location.hostname === 'ibaconnect.info';
    if (!isProduction) {
      toast.info('La conexión con YouVersion solo está habilitada en producción (ibaconnect.info)');
      return;
    }
    setConnecting(true);
    try {
      const redirect_uri = `${window.location.origin}/app/biblia-youversion/oauth-callback`;
      const res = await axios.post(
        `${API}/youversion/auth/start`,
        { redirect_uri, scope: 'openid profile email' }
      );
      // Redirect the whole window to YouVersion auth
      window.location.href = res.data.auth_url;
    } catch (e) {
      console.error(e);
      toast.error('No se pudo iniciar la conexión con YouVersion');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta YouVersion?')) return;
    try {
      await axios.post(`${API}/youversion/auth/disconnect`, {});
      setYvStatus({ connected: false });
      setHighlights(null);
      setShowHighlights(false);
      toast.success('Cuenta YouVersion desconectada');
    } catch (e) {
      console.error(e);
      toast.error('Error al desconectar');
    }
  };

  const loadHighlights = async () => {
    if (highlights) {
      setShowHighlights(!showHighlights);
      return;
    }
    setLoadingHighlights(true);
    setShowHighlights(true);
    try {
      const res = await axios.get(`${API}/youversion/highlights`);
      setHighlights(res.data);
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || 'No se pudieron cargar los highlights';
      if (status === 428) {
        // Needs data-exchange consent
        setHighlights({ data: [], needsAuthorize: true, error: msg });
      } else {
        toast.error(msg);
        setHighlights({ data: [], error: msg });
      }
    } finally {
      setLoadingHighlights(false);
    }
  };

  const authorizeHighlights = async () => {
    try {
      const res = await axios.post(`${API}/youversion/highlights/authorize`, {});
      window.location.href = res.data.approval_url;
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || '';
      const isPermissionMissing = /InvalidApiKeyForGivenResource|Invalid ApiKey/i.test(detail);
      if (isPermissionMissing) {
        toast.error('El permiso de Highlights no está habilitado para esta app en YouVersion. Se necesita que YouVersion lo active.', { duration: 6000 });
        setHighlights({ data: [], permissionUnavailable: true });
      } else {
        toast.error(detail || 'No se pudo iniciar la autorización');
      }
    }
  };

  // If we came back from data-exchange grant, auto-reload highlights
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('refresh') === 'highlights') {
      setShowHighlights(true);
      setHighlights(null);
      // Clean URL and trigger load
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => loadHighlights(), 200);
    }
  }, []);

  // Toggle a bible in/out of the comparison view (max 2 additional)
  const toggleCompareBible = (bId) => {
    if (compareBibleIds.includes(bId)) {
      const next = compareBibleIds.filter((x) => x !== bId);
      setCompareBibleIds(next);
      localStorage.setItem('yv_compare_bibles', JSON.stringify(next));
      setComparePassages((prev) => {
        const c = { ...prev };
        delete c[bId];
        return c;
      });
    } else {
      if (compareBibleIds.length >= 2) {
        toast.info('Máximo 2 versiones de comparación (3 columnas en total)');
        return;
      }
      const next = [...compareBibleIds, bId];
      setCompareBibleIds(next);
      localStorage.setItem('yv_compare_bibles', JSON.stringify(next));
    }
  };

  // Fetch passages for the comparison bibles when main passage / chapter changes
  useEffect(() => {
    if (!passage || !compareBibleIds.length) return;
    const refToFetch = passage.id || `${bookId}.${chapterId}`;
    if (!refToFetch) return;
    compareBibleIds.forEach(async (bId) => {
      // Skip if we already have this exact ref cached in comparePassages
      if (comparePassages[bId]?.id === refToFetch) return;
      // Skip if main passage is the same bible (shouldn't happen but safety)
      if (Number(bId) === Number(bibleId)) return;
      setLoadingCompare((l) => ({ ...l, [bId]: true }));
      try {
        const r = await axios.get(
          `${API}/youversion/biblias/${bId}/pasaje?ref=${encodeURIComponent(refToFetch)}&format=html&include_headings=true`
        );
        setComparePassages((prev) => ({ ...prev, [bId]: r.data }));
      } catch (err) {
        console.error('Compare fetch error', bId, err);
        setComparePassages((prev) => ({
          ...prev,
          [bId]: { reference: refToFetch, content: '<p class="text-sm text-red-500 italic">No disponible en esta versión</p>', error: true },
        }));
      } finally {
        setLoadingCompare((l) => ({ ...l, [bId]: false }));
      }
    });
  }, [passage, compareBibleIds, bibleId]);

  // 1. Load list of bibles on mount
  useEffect(() => {
    const loadBibles = async () => {
      setLoadingBibles(true);
      try {
        const res = await axios.get(`${API}/youversion/biblias?language=spa,eng,por`);
        const list = res.data?.data || [];
        setBibles(list);
        const saved = localStorage.getItem(LS_BIBLE);
        const initial = (saved && list.find(b => String(b.id) === saved)) ? Number(saved) : (list[0]?.id ?? null);
        setBibleId(initial);
      } catch (e) {
        console.error(e);
        toast.error('Error cargando versiones de la Biblia');
      } finally {
        setLoadingBibles(false);
      }
    };
    loadBibles();
  }, []);

  // 2. When bibleId changes → load index
  useEffect(() => {
    if (!bibleId) return;
    const loadIndex = async () => {
      setLoadingIndex(true);
      setIndex(null);
      try {
        const res = await axios.get(`${API}/youversion/biblias/${bibleId}/index`);
        setIndex(res.data);
        localStorage.setItem(LS_BIBLE, String(bibleId));
        const savedBook = localStorage.getItem(LS_BOOK);
        const savedChapter = localStorage.getItem(LS_CHAPTER);
        const books = res.data?.books || [];
        const initBook = (savedBook && books.find(b => b.id === savedBook)) ? savedBook : (books[0]?.id ?? null);
        setBookId(initBook);
        const initChapters = books.find(b => b.id === initBook)?.chapters || [];
        const initChapter = (savedChapter && initChapters.find(c => c.id === savedChapter)) ? savedChapter : (initChapters[0]?.id ?? null);
        setChapterId(initChapter);
      } catch (e) {
        console.error(e);
        toast.error('Error cargando estructura de la Biblia');
      } finally {
        setLoadingIndex(false);
      }
    };
    loadIndex();
  }, [bibleId]);

  // Derived: current book + chapters list
  const currentBook = useMemo(
    () => (index?.books || []).find(b => b.id === bookId),
    [index, bookId]
  );
  const chapters = currentBook?.chapters || [];

  // 3. When bibleId/bookId/chapterId all set → load passage
  useEffect(() => {
    if (!bibleId || !bookId || !chapterId) return;
    const ref = `${bookId}.${chapterId}`;
    const loadPassage = async () => {
      setLoadingPassage(true);
      try {
        const res = await axios.get(
          `${API}/youversion/biblias/${bibleId}/pasaje?ref=${ref}&format=html&include_headings=true`
        );
        setPassage(res.data);
        localStorage.setItem(LS_BOOK, bookId);
        localStorage.setItem(LS_CHAPTER, chapterId);
        // Scroll to top on chapter change
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        console.error(e);
        toast.error('Error cargando pasaje');
        setPassage(null);
      } finally {
        setLoadingPassage(false);
      }
    };
    loadPassage();
  }, [bibleId, bookId, chapterId]);

  const goPrevChapter = () => {
    if (!chapters.length) return;
    const idx = chapters.findIndex(c => c.id === chapterId);
    if (idx > 0) {
      setChapterId(chapters[idx - 1].id);
    } else {
      // Previous book, last chapter
      const books = index?.books || [];
      const bIdx = books.findIndex(b => b.id === bookId);
      if (bIdx > 0) {
        const prevBook = books[bIdx - 1];
        setBookId(prevBook.id);
        setChapterId(prevBook.chapters[prevBook.chapters.length - 1]?.id);
      }
    }
  };

  const goNextChapter = () => {
    if (!chapters.length) return;
    const idx = chapters.findIndex(c => c.id === chapterId);
    if (idx < chapters.length - 1) {
      setChapterId(chapters[idx + 1].id);
    } else {
      const books = index?.books || [];
      const bIdx = books.findIndex(b => b.id === bookId);
      if (bIdx < books.length - 1) {
        const nextBook = books[bIdx + 1];
        setBookId(nextBook.id);
        setChapterId(nextBook.chapters[0]?.id);
      }
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const raw = searchRef.trim();
    if (!raw || !bibleId) return;

    // Acepta dos formatos:
    // 1. Natural en español: "Juan 3:16", "1 Corintios 13:4-7", "Génesis 1"
    // 2. USFM directo: "JHN.3.16", "1CO.13.4-1CO.13.7"
    let usfm = '';
    const refs = findBibleReferences(raw);
    if (refs.length > 0) {
      usfm = refs[0].usfm;
    } else if (/^[1-3]?[A-Z]{2,3}\.\d{1,3}(\.\d{1,3}(-[1-3]?[A-Z]{2,3}\.\d{1,3}\.\d{1,3})?)?$/.test(raw.toUpperCase())) {
      usfm = raw.toUpperCase();
    } else {
      toast.error(`No reconozco "${raw}". Probá con: Juan 3:16, 1 Corintios 13:4-7 o JHN.3.16`);
      return;
    }

    setLoadingPassage(true);
    try {
      const res = await axios.get(
        `${API}/youversion/biblias/${bibleId}/pasaje?ref=${encodeURIComponent(usfm)}&format=html`
      );
      setPassage(res.data);
      // Sync selectors al libro/capítulo del resultado, si los matchea
      const [book, ch] = usfm.split('.');
      if (book && index?.books?.some((b) => b.id === book)) setBookId(book);
      if (ch) setChapterId(String(parseInt(ch, 10)));
    } catch (err) {
      console.error(err);
      toast.error(`Referencia no encontrada: ${raw}`);
    } finally {
      setLoadingPassage(false);
    }
  };

  const copyPassage = async () => {
    if (!passage) return;
    // Strip HTML tags for clipboard
    const plain = (passage.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const text = `${passage.reference}\n\n${plain}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Pasaje copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error('No se pudo copiar');
    }
  };

  const currentBible = bibles.find(b => b.id === bibleId);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4" data-testid="biblia-youversion">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-rose-600 flex items-center justify-center shadow-md">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Biblia YouVersion</h1>
            <p className="text-sm text-gray-500">Lectura oficial con licencia YouVersion Platform</p>
          </div>
        </div>
        {/* Connect / Disconnect */}
        {yvStatus.connected ? (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="yv-connected-badge">
            {yvStatus.profile_picture && (
              <img
                src={yvStatus.profile_picture.startsWith('http') ? yvStatus.profile_picture : `https://${yvStatus.profile_picture}`}
                alt=""
                className="w-7 h-7 rounded-full"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <span className="text-sm font-medium text-amber-900 max-w-[160px] truncate">
              {yvStatus.user_name || yvStatus.user_email || 'YouVersion'}
            </span>
            <button
              data-testid="yv-disconnect-btn"
              onClick={handleDisconnect}
              className="text-amber-700 hover:text-red-600 ml-1"
              title="Desconectar"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-1">
            <Button
              data-testid="yv-connect-btn"
              onClick={handleConnect}
              disabled={connecting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
              Conectar mi cuenta YouVersion
            </Button>
            {typeof window !== 'undefined' && window.location.hostname !== 'ibaconnect.info' && (
              <span className="text-xs text-amber-700 italic">Solo disponible en ibaconnect.info</span>
            )}
          </div>
        )}
      </div>

      {/* Highlights panel: si YouVersion no habilita el permiso, mostramos un mensaje claro
          en lugar de errores raros. La conexión OAuth sigue funcionando. */}
      {yvStatus.connected && (
        <Card>
          <CardContent className="p-4">
            <button
              onClick={loadHighlights}
              className="w-full flex items-center justify-between text-left"
              data-testid="yv-highlights-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <Star className="w-4 h-4 text-amber-600" />
                Mis Highlights de YouVersion
                {highlights?.data && (
                  <span className="text-xs text-gray-500 font-normal">
                    ({highlights.data.length}{highlights.total_size && ` de ${highlights.total_size}`})
                  </span>
                )}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showHighlights ? 'rotate-180' : ''}`} />
            </button>
            {showHighlights && (
              <div className="mt-4 pt-4 border-t" data-testid="yv-highlights-panel">
                {loadingHighlights ? (
                  <div className="text-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" />
                  </div>
                ) : highlights?.permissionUnavailable ? (
                  <div className="text-center py-4 space-y-2">
                    <i className="fas fa-lock text-amber-500 text-2xl" />
                    <p className="text-sm text-amber-800 font-medium">Permiso de Highlights no disponible</p>
                    <p className="text-xs text-gray-500 max-w-sm mx-auto">
                      YouVersion aún no habilitó el permiso de Highlights para esta app.
                      Cuando lo habiliten (es manual desde YouVersion), tus highlights aparecerán acá automáticamente.
                    </p>
                  </div>
                ) : highlights?.needsAuthorize ? (
                  <div className="text-center py-4 space-y-3">
                    <p className="text-sm text-amber-800">{highlights.error}</p>
                    <Button
                      data-testid="yv-authorize-highlights-btn"
                      onClick={authorizeHighlights}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Autorizar Highlights
                    </Button>
                    <p className="text-xs text-gray-500 max-w-sm mx-auto">
                      Vas a ser redirigido a YouVersion para dar consentimiento. Después volvés acá automáticamente.
                    </p>
                  </div>
                ) : highlights?.error ? (
                  <p className="text-sm text-red-600">{highlights.error}</p>
                ) : highlights?.data?.length ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {highlights.data.map((h, idx) => (
                      <div
                        key={h.id || idx}
                        className="p-3 rounded-lg border border-gray-100 hover:border-amber-300 cursor-pointer transition"
                        style={{ backgroundColor: h.color ? `${h.color}22` : '#fef3c7' }}
                        onClick={() => {
                          // Try to navigate to the highlighted reference if it's a usfm reference
                          const ref = h.reference || h.usfm || h.passage_id;
                          if (ref && bibleId) {
                            setSearchRef(ref);
                            setLoadingPassage(true);
                            axios.get(
                              `${API}/youversion/biblias/${bibleId}/pasaje?ref=${encodeURIComponent(ref)}&format=html`
                            ).then(r => setPassage(r.data)).catch(() => toast.error('No se pudo abrir el pasaje'))
                              .finally(() => setLoadingPassage(false));
                          }
                        }}
                      >
                        <div className="text-xs font-semibold text-gray-700">
                          {h.reference || h.usfm || h.passage_id || 'Highlight'}
                        </div>
                        {h.text && (
                          <div className="text-sm text-gray-600 mt-1 line-clamp-2">{h.text}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No tenés highlights en YouVersion todavía</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selectores */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Versión */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Versión</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="yv-version-select"
                    disabled={loadingBibles || bibles.length === 0}
                    className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm hover:border-amber-400 focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="truncate text-left">
                      {(() => {
                        if (!bibleId) return <span className="text-muted-foreground">Cargando...</span>;
                        const b = bibles.find((x) => x.id === bibleId);
                        if (!b) return 'Seleccionar';
                        return `${langFlag(b.language_tag)} ${b.localized_abbreviation || b.abbreviation}`;
                      })()}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-1" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="p-2 w-[calc(100vw-2rem)] sm:w-[520px] md:w-[640px] max-h-[70vh] overflow-y-auto"
                  data-testid="yv-version-grid"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                    {bibles.map((b) => {
                      const isActive = b.id === bibleId;
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => {
                            setBibleId(Number(b.id));
                            // Close popover: dispatch Escape so Radix closes
                            document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
                          }}
                          data-testid={`yv-version-option-${b.id}`}
                          title={b.localized_title || b.title}
                          className={`flex flex-col items-start text-left gap-0.5 rounded-lg border px-2 py-2 text-xs transition ${
                            isActive
                              ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                              : 'bg-white text-gray-800 border-gray-200 hover:border-amber-400 hover:bg-amber-50'
                          }`}
                        >
                          <div className="flex items-center gap-1 font-semibold">
                            <span>{langFlag(b.language_tag)}</span>
                            <span className="truncate">{b.localized_abbreviation || b.abbreviation}</span>
                            {isActive && <CheckIcon className="w-3 h-3 ml-auto shrink-0" />}
                          </div>
                          <span className={`text-[10px] leading-tight line-clamp-2 ${isActive ? 'text-white/90' : 'text-gray-500'}`}>
                            {b.localized_title || b.title}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            {/* Libro */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Libro</label>
              <Select
                value={bookId || ''}
                onValueChange={(v) => {
                  setBookId(v);
                  const b = (index?.books || []).find(x => x.id === v);
                  setChapterId(b?.chapters?.[0]?.id || null);
                }}
                disabled={loadingIndex || !index}
              >
                <SelectTrigger data-testid="yv-book-select" className="w-full">
                  <SelectValue placeholder={loadingIndex ? 'Cargando libros...' : 'Selecciona libro'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(index?.books || []).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Capítulo */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Capítulo</label>
              <Select
                value={chapterId || ''}
                onValueChange={(v) => setChapterId(v)}
                disabled={!chapters.length}
              >
                <SelectTrigger data-testid="yv-chapter-select" className="w-full">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {chapters.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Búsqueda por referencia */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="yv-search-input"
                value={searchRef}
                onChange={(e) => setSearchRef(e.target.value)}
                placeholder="Buscar: Juan 3:16, 1 Corintios 13:4-7, Génesis 1 o USFM (JHN.3.16)"
                className="pl-9"
              />
            </div>
            <Button type="submit" data-testid="yv-search-btn" variant="secondary">
              Buscar
            </Button>
          </form>

          {/* Multi-version comparison selector */}
          {bibles.length > 1 && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-start gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-600 mt-1.5">
                  🔄 Comparar con:
                </span>
                <div className="flex flex-wrap gap-1.5 flex-1">
                  {bibles.filter((b) => b.id !== bibleId).map((b) => {
                    const isActive = compareBibleIds.includes(b.id);
                    const disabled = !isActive && compareBibleIds.length >= 2;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => toggleCompareBible(b.id)}
                        disabled={disabled}
                        data-testid={`yv-compare-toggle-${b.id}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${
                          isActive
                            ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                            : disabled
                              ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400 hover:text-amber-700'
                        }`}
                        title={disabled ? 'Máximo 2 versiones de comparación' : ''}
                      >
                        {isActive ? '✓ ' : '+ '}{langFlag(b.language_tag)} {b.localized_abbreviation || b.abbreviation}
                      </button>
                    );
                  })}
                </div>
                {compareBibleIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setCompareBibleIds([]);
                      setComparePassages({});
                      localStorage.setItem('yv_compare_bibles', '[]');
                    }}
                    className="text-xs text-gray-500 hover:text-red-600 underline mt-1.5"
                    data-testid="yv-compare-clear"
                  >
                    Limpiar
                  </button>
                )}
              </div>
              {compareBibleIds.length > 0 && (
                <p className="text-[10px] text-gray-400 mt-1.5">
                  Comparando {compareBibleIds.length + 1} versiones · Máx 3 columnas
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lector */}
      <Card>
        <CardContent className={`min-h-[60vh] ${compareBibleIds.length > 0 ? 'p-3 sm:p-5' : 'p-6 sm:p-10'}`}>
          {loadingPassage ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : passage ? (
            compareBibleIds.length === 0 ? (
              <div>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900" data-testid="yv-passage-reference">
                      {passage.reference}
                    </h2>
                    {currentBible && (
                      <p className="text-xs text-gray-400 mt-1">
                        {currentBible.localized_title || currentBible.title}
                      </p>
                    )}
                  </div>
                  <Button
                    data-testid="yv-copy-btn"
                    variant="ghost"
                    size="sm"
                    onClick={copyPassage}
                    className="text-gray-500"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <article
                  data-testid="yv-passage-content"
                  className="prose prose-lg max-w-none font-serif leading-relaxed text-gray-800 yv-passage"
                  dangerouslySetInnerHTML={{ __html: passage.content || '' }}
                />
              </div>
            ) : (
              // Multi-version comparison view (2-3 columns on desktop, stacked on mobile)
              <div>
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4" data-testid="yv-passage-reference">
                  {passage.reference}
                </h2>
                <div className={`grid gap-4 grid-cols-1 ${compareBibleIds.length === 1 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`} data-testid="yv-compare-grid">
                  {/* Primary column */}
                  <div className="border-l-4 border-amber-500 pl-3">
                    <div className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
                      {langFlag(currentBible?.language_tag)} {currentBible?.localized_abbreviation || currentBible?.abbreviation || '—'}
                    </div>
                    <article
                      data-testid="yv-passage-content"
                      className="prose prose-sm max-w-none font-serif leading-relaxed text-gray-800 yv-passage"
                      dangerouslySetInnerHTML={{ __html: passage.content || '' }}
                    />
                  </div>
                  {/* Comparison columns */}
                  {compareBibleIds.map((bId) => {
                    const b = bibles.find((x) => x.id === bId);
                    const cp = comparePassages[bId];
                    return (
                      <div key={bId} className="border-l-4 border-gray-300 pl-3 relative" data-testid={`yv-compare-column-${bId}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                            {langFlag(b?.language_tag)} {b?.localized_abbreviation || b?.abbreviation || `Bible ${bId}`}
                          </div>
                          <button
                            onClick={() => toggleCompareBible(bId)}
                            className="text-gray-300 hover:text-red-500"
                            title="Quitar"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {loadingCompare[bId] ? (
                          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
                        ) : cp ? (
                          <article
                            className="prose prose-sm max-w-none font-serif leading-relaxed text-gray-800 yv-passage"
                            dangerouslySetInnerHTML={{ __html: cp.content || '' }}
                          />
                        ) : (
                          <p className="text-xs text-gray-400 italic py-4">Cargando...</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : (
            <div className="text-center text-gray-400 py-20">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Selecciona una versión, libro y capítulo</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navegación */}
      {passage && (
        <div className="flex items-center justify-between sticky bottom-4">
          <Button
            data-testid="yv-prev-btn"
            variant="outline"
            onClick={goPrevChapter}
            className="shadow-md bg-white"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>
          <Button
            data-testid="yv-next-btn"
            variant="outline"
            onClick={goNextChapter}
            className="shadow-md bg-white"
          >
            Siguiente <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Footer copyright */}
      {currentBible?.copyright && (
        <p className="text-xs text-gray-400 text-center mt-6 px-4">
          {currentBible.copyright}
        </p>
      )}

      <style>{`
        .yv-passage p { margin-bottom: 0.75em; }
        .yv-passage sup, .yv-passage .verse-label, .yv-passage .v {
          color: #d97706; font-weight: 600; font-size: 0.75em;
          vertical-align: super; margin-right: 0.25em;
        }
        .yv-passage h1, .yv-passage h2, .yv-passage h3 {
          font-weight: 600; color: #92400e; margin-top: 1.5em; margin-bottom: 0.5em;
        }
      `}</style>
    </div>
  );
};

export default BibliaYouVersion;
